import { Color, Group, Matrix, Path, Point, Project } from "paper";
import { PaperUtils } from "../utilities/paper-utils";
import { defaultGridLineSettings, defaultGridTextSettings, defaultEntityPathSettings, } from "../settings/altitude-view-settings";
import { AssetType } from "../types";
const MUTED_COLOR = "#999999";
function getOutlineColor(outline) {
    switch (outline.assetType) {
        case AssetType.Point_Cloud:
            return "#00FF00";
        case AssetType.Design_Data:
            return "#FF00FF";
        case AssetType.Road_Data:
        case AssetType.Flat_Ground_Data:
            return "#00FFFF";
        case AssetType.REFERENCE_TOPOLOGY:
            return "#7B3F00";
        case AssetType.Spreading_Compaction_Data:
            return outline.emphasized ? "#FF8A65" : MUTED_COLOR;
    }
}
export class MeasurementView {
    _project;
    _view;
    _canvas;
    _zoomFactor = 1.05;
    _scaleFactor;
    _outlineMap = [];
    // to keep all grid lines together and easy to delete all grid lines at same time.
    _gridGroup;
    _gridSizeInPixel = 50;
    _outlineRenderGroup;
    _gridText;
    isDragging = false;
    startPoint;
    _currentEntityID;
    _presentation;
    get currentEntityID() {
        return this._currentEntityID;
    }
    constructor(presentation) {
        this._presentation = presentation;
        this._canvas = document.createElement("canvas");
        // Setup canvas here
        this._canvas.style.width = "100%";
        this._canvas.style.height = "100%";
        this._canvas.style.backgroundColor = "#566573";
        this._project = new Project(this._canvas);
        this._project.activate();
        this._view = this._project.view;
        this._view.transform(new Matrix(1, 0, 0, -1, 0, 0));
        //to keep all grid lines in one group
        this._gridGroup = new Group();
        this._gridText = new Group();
        //Create grid lines
        this.changeGridSize(this._gridSizeInPixel);
        this._canvas.addEventListener("wheel", (event) => {
            PaperUtils.zoom(event, this._zoomFactor, this._view);
            this.changeGridSize(this._gridSizeInPixel);
            this._attenuate();
        });
        this._canvas.addEventListener("mousedown", (event) => {
            this.isDragging = true;
            this.startPoint = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
        });
        this._canvas.addEventListener("mousemove", (event) => {
            if (this.isDragging) {
                const point = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
                this._view.translate(point.subtract(this.startPoint));
                this.changeGridSize(this._gridSizeInPixel);
                this._view.update();
            }
        });
        this._canvas.addEventListener("mouseup", () => {
            this.isDragging = false;
        });
    }
    _activateView() {
        this._project.activate();
    }
    clearView() {
        if (this._outlineRenderGroup) {
            this._outlineRenderGroup.removeChildren();
            this._outlineRenderGroup = undefined;
        }
        //remove grid text
        this._gridText.removeChildren();
        this._currentEntityID = undefined;
    }
    _attenuate() {
        //attenuate Entities
        this._outlineRenderGroup &&
            this._outlineRenderGroup.children.forEach((l) => PaperUtils.attenuateLines(l, this._view, defaultEntityPathSettings.defaultPixelSize));
        //attenuate Grids
        this._gridGroup &&
            this._gridGroup.children.forEach((line) => PaperUtils.attenuateLines(line, this._view, defaultGridLineSettings.defaultPixelSize));
        this._gridText &&
            this._gridText.children.forEach((text) => {
                PaperUtils.attenuateEntites(text, this._view, defaultGridTextSettings.defaultPixelSize);
            });
        this._view.requestUpdate();
    }
    resizeView(size) {
        if (size.height && size.width) {
            this._project.view.viewSize.height = size.height;
            this._project.view.viewSize.width = size.width;
            this._view.update();
            this.changeGridSize(this._gridSizeInPixel);
        }
    }
    _createPath(pointArr, color) {
        const path = new Path();
        path.strokeColor = new Color(color);
        pointArr.forEach((arr) => {
            path.add(new Point(arr[0], arr[1]));
        });
        return path;
    }
    renderData(measurementID, data, fit) {
        this._activateView();
        this.clearView();
        if (measurementID === undefined || data === undefined) {
            return;
        }
        this._currentEntityID = measurementID;
        this._outlineMap = data;
        const rect = this._canvas.getBoundingClientRect();
        this.resizeView({ width: rect.width, height: rect.height });
        this._outlineRenderGroup = new Group();
        this._outlineMap.forEach((outline) => {
            this._outlineRenderGroup.addChild(this._createPath(outline.outline2D, getOutlineColor(outline)));
        });
        if (fit) {
            //fit the entities in current view
            const bounds = this._outlineRenderGroup.bounds;
            PaperUtils.fitView(this._view, bounds.center, bounds.width, bounds.height);
        }
        this.changeGridSize(this._gridSizeInPixel);
        this._attenuate();
    }
    setBackgroundColor(color) {
        const tempColor = color ? color : "#566573";
        this._canvas.style.backgroundColor = tempColor;
    }
    updatePresentation(presentation) {
        this._presentation = presentation;
        if (this._currentEntityID && this._outlineMap.length > 0) {
            this.renderData(this._currentEntityID, this._outlineMap, false);
        }
        else {
            this._activateView();
            this.changeGridSize(this._gridSizeInPixel);
        }
    }
    changeGridSize(step) {
        this._activateView();
        PaperUtils.createGrid(this._view, step, this._gridGroup, this._gridText, this._presentation);
        this._attenuate();
    }
    getCanvas() {
        return this._canvas;
    }
}
//# sourceMappingURL=view-2d-measurement.js.map